// Phase G — Pending upgrade trigger lookup.
//
// Polls free_tier_conversion_triggers for the current user, finds the
// most recent un-converted, un-dismissed trigger of an upgrade-relevant
// kind, and returns it alongside dismiss() / markConverted() actions.
//
// Doctrine: never fabricate. When no qualifying row exists the hook
// returns trigger=null and the modal stays hidden.

import { useCallback, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import { useTableQuery } from "../data/useTableQuery.ts";

export type UpgradeTriggerKind =
  | "free_acca_won_3_in_row"
  | "super_acca_view_count_5"
  | "daily_acca_engaged_3_days"
  | "sika_simulator_completed";

const UPGRADE_KINDS: readonly UpgradeTriggerKind[] = [
  "free_acca_won_3_in_row",
  "super_acca_view_count_5",
  "daily_acca_engaged_3_days",
  "sika_simulator_completed",
];

export interface PendingTrigger {
  id: string;
  trigger_kind: UpgradeTriggerKind;
  trigger_meta: Record<string, unknown> | null;
  fired_at: string;
}

export interface PendingUpgradeTriggerState {
  trigger: PendingTrigger | null;
  loading: boolean;
  dismiss: () => Promise<void>;
  markConverted: () => Promise<void>;
  refetch: () => Promise<void>;
}

function isUpgradeKind(k: unknown): k is UpgradeTriggerKind {
  return typeof k === "string" && (UPGRADE_KINDS as readonly string[]).includes(k);
}

// Applies the legacy client-side filter to a fetched row set: most-recent
// (already ordered) row of an upgrade kind whose meta has no dismissed_at.
function pickFresh(rows: unknown): PendingTrigger | null {
  if (!Array.isArray(rows)) return null;
  const fresh = (rows as Array<Record<string, unknown>>).find((row) => {
    const meta = (row["trigger_meta"] as Record<string, unknown> | null) ?? null;
    const dismissedAt = meta && typeof meta["dismissed_at"] === "string"
      ? meta["dismissed_at"]
      : null;
    return !dismissedAt && isUpgradeKind(row["trigger_kind"]);
  });
  if (!fresh) return null;
  return {
    id: String(fresh["id"]),
    trigger_kind: fresh["trigger_kind"] as UpgradeTriggerKind,
    trigger_meta: (fresh["trigger_meta"] as Record<string, unknown> | null) ?? null,
    fired_at: String(fresh["fired_at"] ?? ""),
  };
}

export function usePendingUpgradeTrigger(): PendingUpgradeTriggerState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const userId = user?.id ?? null;
  // IDs the user just acted on (dismiss / markConverted). Legacy set
  // trigger=null immediately after the mutation; we suppress that id locally
  // until the next read drops it (converted_at / dismissed_at now set).
  const [actedIds, setActedIds] = useState<ReadonlySet<string>>(() => new Set());

  // Read error is SUPPRESSED (legacy: on error → trigger=null, no error field).
  // useTableQuery throws on error → q.data undefined → trigger null. Same shape.
  const q = useTableQuery<Array<Record<string, unknown>>, PendingTrigger | null>(
    ["pending-upgrade-trigger", userId],
    (client) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (client as any)
        .from("free_tier_conversion_triggers")
        .select("id, trigger_kind, trigger_meta, fired_at, converted_at")
        .eq("user_id", userId)
        .is("converted_at", null)
        .in("trigger_kind", UPGRADE_KINDS)
        .order("fired_at", { ascending: false })
        .limit(20),
    { enabled: Boolean(userId), select: pickFresh },
  );

  const refetch = useCallback(async (): Promise<void> => {
    await q.refetch();
  }, [q]);

  // Derived trigger: query result, minus anything the user just acted on.
  const trigger = useMemo<PendingTrigger | null>(() => {
    const picked = q.data ?? null;
    if (picked && actedIds.has(picked.id)) return null;
    return picked;
  }, [q.data, actedIds]);

  const dismiss = useCallback(async () => {
    if (!trigger) return;
    const id = trigger.id;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    await sb.rpc("dismiss_conversion_trigger", { p_id: id });
    setActedIds((prev) => new Set(prev).add(id));
    void q.refetch();
  }, [supabase, trigger, q]);

  const markConverted = useCallback(async () => {
    if (!trigger || !userId) return;
    const id = trigger.id;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    await sb
      .from("free_tier_conversion_triggers")
      .update({ converted_at: new Date().toISOString() })
      .eq("id", id)
      .eq("user_id", userId);
    setActedIds((prev) => new Set(prev).add(id));
    void q.refetch();
  }, [supabase, trigger, userId, q]);

  const loading = Boolean(userId) && q.isLoading;

  return useMemo(
    () => ({ trigger, loading, dismiss, markConverted, refetch }),
    [trigger, loading, dismiss, markConverted, refetch],
  );
}
